import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import NeedHelpTickets from "./NeedHelpTickets";
import NeedHelpTicketModal from "./NeedHelpTicketModal";
import PopUp from "../Popup/Popup";
import decodeToken from "../../lib/decodeToken";
import { markNeedHelpReturn } from "../../lib/needHelpReturn";
// The same overlay + sheet classes the Need Help modal uses, so the page has
// exactly the same look: dimmed/blurred backdrop and the narrower sheet.
import modalStyles from "./NeedHelpModal.module.css";

const DEFAULT_RETURN_ROUTE = "/fast-agent-detail";
// Only these pages can send the user here, so only these are valid return targets.
const ALLOWED_RETURN_ROUTES = ["/dashboard", "/fast-agent-detail"];

const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch (_e) {
    return "";
  }
};

// Route page for the Need Help Tickets screen (/need-help-tickets).
// Leaving it for the home page, by the back arrow or the browser back button,
// makes the Need Help modal open again there.
const NeedHelpTicketsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The page the user opened Tickets from (set in NeedHelpModal). Falls back
  // to /fast-agent-detail if the state is missing or not an allowed route.
  const returnRoute = ALLOWED_RETURN_ROUTES.includes(location.state?.from)
    ? location.state.from
    : DEFAULT_RETURN_ROUTE;
  
  // Need Help is owner-only. A team member who opens this URL directly is
  // sent back. Read from localStorage like the permission hooks do, so
  // there is no extra polling on this page.
  const isTeamMember = !!localStorage.getItem("teamOwnerId");

  useEffect(() => {
    if (isTeamMember) navigate(returnRoute, { replace: true });
  }, [isTeamMember, navigate, returnRoute]);
  const userId = useMemo(() => decodeToken(getToken())?.id || "", []);

  const [ticketFormOpen, setTicketFormOpen] = useState(false);
  // CHANGED: was a true/false "ticketCreatedPopup". It now holds the text of
  // the success popup ("" = hidden), so the same popup serves both
  // "Ticket created" and "Attachments uploaded".
  const [popupMessage, setPopupMessage] = useState("");
  // Bumped after a ticket is created, so the list loads again.
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(
    () => () => {
      if (window.location.pathname === returnRoute) markNeedHelpReturn();
    },
    [returnRoute],
  );

  // CHANGED: hides whatever message is showing after 1.8 seconds
  useEffect(() => {
    if (!popupMessage) return undefined;
    const timeout = setTimeout(() => setPopupMessage(""), 1800);
    return () => clearTimeout(timeout);
  }, [popupMessage]);

  const goBack = useCallback(() => {
    navigate(returnRoute, { replace: true });
  }, [navigate, returnRoute]);

  if (isTeamMember) return null;
  
  return (
    <div className={modalStyles.overlay}>
      <div className={modalStyles.sheet}>
        <NeedHelpTickets
          isOpen
          userId={userId}
          refreshKey={refreshKey}
          onClose={goBack}
          onCreateTicket={() => setTicketFormOpen(true)}
          onAttachmentsUploaded={() => setPopupMessage("Attachments uploaded")} // ADDED
        />

        {ticketFormOpen && (
          <NeedHelpTicketModal
            isOpen={ticketFormOpen}
            userId={userId}
            onClose={() => setTicketFormOpen(false)}
            onSuccess={() => {
              setTicketFormOpen(false);
              setPopupMessage("Ticket created"); // CHANGED
              setRefreshKey((k) => k + 1);
            }}
          />
        )}
      </div>

      <PopUp
        type="success"
        message={popupMessage}
        onClose={() => setPopupMessage("")}
      />
    </div>
  );
};

export default NeedHelpTicketsPage;
